import { TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';
import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { map, Observable, startWith } from 'rxjs';

export interface CustomerFilterValue {
  agente: string;
  agenteId: string;
  ispettore: string;
  ispettoreId: string;
  tipoZona: string[];
  zona: string;
  zonaId: string;
  dataUltimaFattura: Date | null;
  newCustomer: boolean;
}

export interface CustomerZoneOption {
  id: string;
  nome: string;
}

export interface CustomerLookupOption {
  value: string;
  label: string;
}

export interface CustomerFilterOptions {
  zona: CustomerZoneOption[];
  tipoZona: string[];
  agente: CustomerLookupOption[];
  ispettore: CustomerLookupOption[];
}

interface CustomerFilterDialogData {
  value: CustomerFilterValue;
  options: CustomerFilterOptions;
}

@Component({
  providers: [provideTranslocoScope('receipts')],
  selector: 'rev-receipts-filter-dialog-component',
  imports: [TranslocoPipe,
    AsyncPipe,
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    ReactiveFormsModule,
  ],
  templateUrl: './receipts-filter-dialog-component.html',
  styleUrl: './receipts-filter-dialog-component.scss',
})
export class receiptsFilterDialogComponent {
  readonly filterForm: FormGroup;
  readonly filteredZona: Observable<CustomerZoneOption[]>;
  readonly filteredTipoZona: Observable<string[]>;
  readonly filteredAgente: Observable<CustomerLookupOption[]>;
  readonly filteredIspettore: Observable<CustomerLookupOption[]>;
  protected readonly dialogData = inject<CustomerFilterDialogData>(MAT_DIALOG_DATA);

  constructor(
    formBuilder: FormBuilder,
    private readonly dialogRef: MatDialogRef<receiptsFilterDialogComponent>,
  ) {
    this.filterForm = formBuilder.group({
      agente: [this.dialogData.value.agente ?? ''],
      agenteId: [this.dialogData.value.agenteId ?? ''],
      ispettore: [this.dialogData.value.ispettore ?? ''],
      ispettoreId: [this.dialogData.value.ispettoreId ?? ''],
      tipoZona: [{ value: this.dialogData.value.tipoZona ?? [], disabled: true }],
      zona: [this.dialogData.value.zona ?? ''],
      zonaId: [this.dialogData.value.zonaId ?? ''],
      dataUltimaFattura: [this.dialogData.value.dataUltimaFattura ?? null],
      newCustomer: [this.dialogData.value.newCustomer ?? false],
    });

    this.filteredZona = this.filterForm.get('zona')!.valueChanges.pipe(
      startWith(this.filterForm.get('zona')!.value ?? ''),
      map(value => this.filterZoneOptions(value)),
    );
    this.filteredTipoZona = this.filterForm.get('tipoZona')!.valueChanges.pipe(
      startWith(this.filterForm.get('tipoZona')!.value ?? []),
      map(value => this.filterStringOptions(this.dialogData.options.tipoZona, value)),
    );
    this.filteredAgente = this.filterForm.get('agente')!.valueChanges.pipe(
      startWith(this.filterForm.get('agente')!.value ?? ''),
      map(value => this.filterLookupOptions(this.dialogData.options.agente, value)),
    );
    this.filteredIspettore = this.filterForm.get('ispettore')!.valueChanges.pipe(
      startWith(this.filterForm.get('ispettore')!.value ?? ''),
      map(value => this.filterLookupOptions(this.dialogData.options.ispettore, value)),
    );

    this.clearIdWhenLabelChanges('zona', 'zonaId', this.dialogData.options.zona, 'nome', 'id');
    this.clearIdWhenLabelChanges('agente', 'agenteId', this.dialogData.options.agente, 'label', 'value');
    this.clearIdWhenLabelChanges('ispettore', 'ispettoreId', this.dialogData.options.ispettore, 'label', 'value');
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onReset(): void {
    this.dialogRef.close({ action: 'reset' });
  }

  onApply(): void {
    this.dialogRef.close({ action: 'apply', value: this.filterForm.getRawValue() });
  }

  onZonaSelected(zone: CustomerZoneOption): void {
    this.filterForm.patchValue({ zona: zone.nome, zonaId: zone.id });
  }

  onAgenteSelected(agente: CustomerLookupOption): void {
    this.filterForm.patchValue({ agente: agente.label, agenteId: agente.value });
  }

  onIspettoreSelected(ispettore: CustomerLookupOption): void {
    this.filterForm.patchValue({ ispettore: ispettore.label, ispettoreId: ispettore.value });
  }

  private filterZoneOptions(value: string | null): CustomerZoneOption[] {
    const search = (value ?? '').toLowerCase();
    return this.dialogData.options.zona.filter(option => option.nome.toLowerCase().includes(search));
  }

  private filterStringOptions(options: string[], value: string | string[] | null): string[] {
    const search = Array.isArray(value) ? value.join(' ') : (value ?? '');
    return options.filter(option => option.toLowerCase().includes(search.toLowerCase()));
  }

  private filterLookupOptions(options: CustomerLookupOption[], value: string | null): CustomerLookupOption[] {
    const search = (value ?? '').toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(search));
  }

  private clearIdWhenLabelChanges<T extends object>(
    labelControlName: string,
    idControlName: string,
    options: T[],
    labelProperty: keyof T,
    idProperty: keyof T,
  ): void {
    this.filterForm.get(labelControlName)!.valueChanges.subscribe(value => {
      const selected = options.find(option => option[idProperty] === this.filterForm.get(idControlName)!.value);
      if (selected?.[labelProperty] !== value) {
        this.filterForm.get(idControlName)!.setValue('', { emitEvent: false });
      }
    });
  }
}
